import React, { useEffect, useState } from "react";
import axios from "axios";
import { useApp, API } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, BellRing, Trash2, Calendar as CalIcon, List, LayoutGrid, ChevronLeft, ChevronRight, User, Users } from "lucide-react";
import { toast } from "sonner";

export default function Appointments() {
  const { t, user } = useApp();
  const isDoctor = user?.role === "doctor";
  const [appts, setAppts] = useState([]);
  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ patient_id: "", date: "", reason: "", doctor: "" });
  const [doctors, setDoctors] = useState([]);
  const [view, setView] = useState("list"); // list | calendar
  const [mine, setMine] = useState(isDoctor); // doctors default to "mes RDV"
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const load = async () => {
    const [a, p, st] = await Promise.all([
      axios.get(`${API}/appointments`, { params: { mine } }),
      axios.get(`${API}/patients`, { params: { page_size: 10000 } }),
      axios.get(`${API}/staff`).catch(() => ({ data: [] })),
    ]);
    setDoctors((st.data || []).filter((u) => u.role === "doctor" && u.active !== false));
    setAppts(a.data);
    setPatients(p.data.items || p.data);
  };

  useEffect(() => { load(); }, [mine]); // reload when toggle changes

  const submit = async (e) => {
    e.preventDefault();
    try {
      // Force Kinshasa (Africa/Kinshasa = UTC+1, no DST) so the stored ISO always matches
      // what the user typed, regardless of their browser timezone.
      const isoDate = new Date(form.date + ":00+01:00").toISOString();
      await axios.post(`${API}/appointments`, { ...form, date: isoDate });
      toast.success("RDV enregistré + confirmation SMS (MOCK)");
      setOpen(false);
      setForm({ patient_id: "", date: "", reason: "", doctor: "" });
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  const sendReminder = async (id, kind) => {
    try {
      await axios.post(`${API}/appointments/${id}/send-reminder?kind=${kind}`);
      toast.success(`Rappel ${kind} envoyé (MOCK SMS)`);
      load();
    } catch {
      toast.error("Erreur envoi");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer ?")) return;
    await axios.delete(`${API}/appointments/${id}`);
    load();
  };

  const fmt = (d) => new Date(d).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short", timeZone: "Africa/Kinshasa" });

  // Local-date-only key (avoids UTC drift bugs on cell keys)
  const localDayKey = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

  // Calendar helpers
  const monthLabel = month.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
  const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  const dayOfWeekStart = (firstDay.getDay() + 6) % 7; // Monday first
  const totalDays = lastDay.getDate();
  const calendarCells = [];
  for (let i = 0; i < dayOfWeekStart; i++) calendarCells.push(null);
  for (let d = 1; d <= totalDays; d++) calendarCells.push(new Date(month.getFullYear(), month.getMonth(), d));
  while (calendarCells.length % 7 !== 0) calendarCells.push(null);

  // Group appts by the LOCAL day their date falls on (respecting the user browser tz),
  // so an appointment stored at 12:00Z (=13:00 CAT / UTC+1) is grouped under its Kinshasa day.
  const apptsByDate = {};
  for (const a of appts) {
    if (!a.date) continue;
    const dt = new Date(a.date);
    if (isNaN(dt)) continue;
    const key = localDayKey(dt);
    (apptsByDate[key] = apptsByDate[key] || []).push(a);
  }
  const today = localDayKey(new Date());
  const prevMonth = () => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1));
  const nextMonth = () => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1));
  const goToday = () => { const d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)); };

  const categoryColor = (reason) => {
    const r = (reason || "").toLowerCase();
    if (r.includes("vaccin") || r.includes("pédiatr") || r.includes("enfant") || r.includes("bcg")) return "#2E7D32";
    if (r.includes("prénatal") || r.includes("grossesse") || r.includes("écho")) return "#D96C4A";
    if (r.includes("diabèt") || r.includes("tension") || r.includes("cardio") || r.includes("hyperten")) return "#1E4631";
    return "#5C6B61";
  };

  return (
    <div className="space-y-6" data-testid="appointments-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-[Outfit] font-bold">{t.dash.appointments}</h1>
        <div className="flex items-center gap-2">
          {/* Mine/All scope toggle */}
          <div className="inline-flex bg-white border border-[#E5EBE7] rounded-lg p-1" data-testid="scope-toggle">
            <button onClick={()=>setMine(true)} className={`px-3 py-1.5 text-xs font-medium rounded inline-flex items-center gap-1.5 transition ${mine ? "bg-[#D96C4A] text-white" : "text-[#5C6B61] hover:text-[#1E4631]"}`} data-testid="scope-mine">
              <User className="w-3.5 h-3.5"/> Mes RDV
            </button>
            <button onClick={()=>setMine(false)} className={`px-3 py-1.5 text-xs font-medium rounded inline-flex items-center gap-1.5 transition ${!mine ? "bg-[#D96C4A] text-white" : "text-[#5C6B61] hover:text-[#1E4631]"}`} data-testid="scope-all">
              <Users className="w-3.5 h-3.5"/> Tout l&apos;hôpital
            </button>
          </div>
          {/* View toggle */}
          <div className="inline-flex bg-white border border-[#E5EBE7] rounded-lg p-1" data-testid="view-toggle">
            <button onClick={()=>setView("list")} className={`px-3 py-1.5 text-xs font-medium rounded inline-flex items-center gap-1.5 transition ${view==="list" ? "bg-[#1E4631] text-white" : "text-[#5C6B61] hover:text-[#1E4631]"}`} data-testid="view-list">
              <List className="w-3.5 h-3.5"/> Liste
            </button>
            <button onClick={()=>setView("calendar")} className={`px-3 py-1.5 text-xs font-medium rounded inline-flex items-center gap-1.5 transition ${view==="calendar" ? "bg-[#1E4631] text-white" : "text-[#5C6B61] hover:text-[#1E4631]"}`} data-testid="view-calendar">
              <LayoutGrid className="w-3.5 h-3.5"/> Calendrier
            </button>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1E4631] hover:bg-[#163424] text-white" data-testid="add-appt-btn">
              <Plus className="w-4 h-4 mr-2"/> {t.dash.new_appt}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t.dash.new_appt}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="appt-form">
              <div>
                <Label>{t.dash.patient}</Label>
                <Select value={form.patient_id} onValueChange={(v)=>setForm({...form, patient_id: v})}>
                  <SelectTrigger data-testid="appt-patient"><SelectValue placeholder="Sélectionner..."/></SelectTrigger>
                  <SelectContent>
                    {patients.map((p)=>(
                      <SelectItem key={p.id} value={p.id}>{p.name} - {p.phone}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>{t.dash.date}</Label><Input type="datetime-local" value={form.date} onChange={(e)=>setForm({...form, date: e.target.value})} required data-testid="appt-date"/></div>
              <div><Label>{t.dash.reason}</Label><Input value={form.reason} onChange={(e)=>setForm({...form, reason: e.target.value})} required data-testid="appt-reason"/></div>
              <div>
                <Label>{t.dash.doctor}</Label>
                {doctors.length > 0 ? (
                  <Select value={form.doctor} onValueChange={(v)=>setForm({...form, doctor: v})}>
                    <SelectTrigger data-testid="appt-doctor"><SelectValue placeholder="Choisir un médecin (optionnel)"/></SelectTrigger>
                    <SelectContent>
                      {doctors.map((d)=>(
                        <SelectItem key={d.id} value={d.name}>{d.name}{d.specialty ? ` — ${d.specialty}` : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={form.doctor} onChange={(e)=>setForm({...form, doctor: e.target.value})} data-testid="appt-doctor"/>
                )}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={()=>setOpen(false)}>{t.dash.cancel}</Button>
                <Button type="submit" className="bg-[#1E4631] hover:bg-[#163424]" data-testid="appt-save">{t.dash.save}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {view === "calendar" ? (
        <div className="bg-white rounded-xl border border-[#E5EBE7] p-5" data-testid="calendar-view">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <button onClick={prevMonth} className="p-2 rounded-lg hover:bg-[#FDFBF7] text-[#5C6B61]" data-testid="cal-prev"><ChevronLeft className="w-4 h-4"/></button>
              <button onClick={nextMonth} className="p-2 rounded-lg hover:bg-[#FDFBF7] text-[#5C6B61]" data-testid="cal-next"><ChevronRight className="w-4 h-4"/></button>
              <h2 className="text-xl font-[Outfit] font-bold capitalize ml-2">{monthLabel}</h2>
            </div>
            <Button variant="outline" size="sm" onClick={goToday} data-testid="cal-today">Aujourd&apos;hui</Button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-xs uppercase tracking-wider text-[#5C6B61] mb-2">
            {["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"].map((d) => (
              <div key={d} className="text-center py-2 font-semibold">{d}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {calendarCells.map((d, i) => {
              if (!d) return <div key={i} className="aspect-square bg-[#FDFBF7]/40 rounded-lg"/>;
              const key = localDayKey(d);
              const dayAppts = apptsByDate[key] || [];
              const isToday = key === today;
              return (
                <div key={i} className={`aspect-square rounded-lg border p-1.5 overflow-hidden flex flex-col gap-1 ${isToday ? "border-[#D96C4A] bg-[#D96C4A]/5" : "border-[#E5EBE7] bg-white"} hover:border-[#1E4631]/30 transition`} data-testid={`cal-day-${key}`}>
                  <div className={`text-xs font-bold ${isToday ? "text-[#D96C4A]" : "text-[#1A1D1A]"}`}>{d.getDate()}</div>
                  <div className="flex-1 overflow-y-auto space-y-0.5">
                    {dayAppts.slice(0, 3).map((a) => {
                      const time = new Date(a.date).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", timeZone: "Africa/Kinshasa" });
                      const color = categoryColor(a.reason);
                      return (
                        <div key={a.id} title={`${time} · ${a.patient_name} · ${a.reason}`}
                             className="text-[10px] px-1.5 py-0.5 rounded truncate cursor-default"
                             style={{ background: `${color}15`, color, borderLeft: `2px solid ${color}` }}
                             data-testid={`cal-appt-${a.id}`}>
                          <strong>{time}</strong> {a.patient_name}
                        </div>
                      );
                    })}
                    {dayAppts.length > 3 && (
                      <div className="text-[10px] text-[#5C6B61] font-medium px-1.5">+{dayAppts.length - 3} autre(s)</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-[#5C6B61]">
            <span>Légende :</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#D96C4A]/20 border-l-2 border-[#D96C4A]"/> Prénatal / grossesse</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#2E7D32]/20 border-l-2 border-[#2E7D32]"/> Pédiatrie / vaccins</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#1E4631]/20 border-l-2 border-[#1E4631]"/> Suivi chronique</span>
          </div>
        </div>
      ) : (
      <div className="bg-white rounded-xl border border-[#E5EBE7] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FDFBF7] text-left text-xs uppercase text-[#5C6B61] tracking-wider">
            <tr>
              <th className="px-5 py-3">{t.dash.patient}</th>
              <th className="px-5 py-3">{t.dash.date}</th>
              <th className="px-5 py-3">{t.dash.reason}</th>
              <th className="px-5 py-3">{t.dash.doctor}</th>
              <th className="px-5 py-3">{t.dash.reminders}</th>
              <th className="px-5 py-3">{t.dash.actions}</th>
            </tr>
          </thead>
          <tbody>
            {appts.length === 0 ? (
              <tr><td colSpan="6" className="text-center py-12 text-[#5C6B61]" data-testid="empty-appts">Aucun rendez-vous</td></tr>
            ) : appts.map((a) => (
              <tr key={a.id} className="border-t border-[#E5EBE7] hover:bg-[#FDFBF7]/50" data-testid={`appt-row-${a.id}`}>
                <td className="px-5 py-3 font-medium">{a.patient_name}<div className="text-xs text-[#5C6B61]">{a.patient_phone}</div></td>
                <td className="px-5 py-3"><span className="inline-flex items-center gap-1.5"><CalIcon className="w-3.5 h-3.5 text-[#5C6B61]"/>{fmt(a.date)}</span></td>
                <td className="px-5 py-3">{a.reason}</td>
                <td className="px-5 py-3">{a.doctor || "—"}</td>
                <td className="px-5 py-3 text-xs">
                  <span className="inline-block px-2 py-0.5 rounded-full bg-[#1E4631]/10 text-[#1E4631]">{a.reminders_sent.length}</span>
                </td>
                <td className="px-5 py-3">
                  <div className="flex gap-2">
                    <button onClick={()=>sendReminder(a.id, "J-15")} className="px-2 py-1 text-xs rounded bg-[#1E4631]/10 text-[#1E4631] hover:bg-[#1E4631]/20" data-testid={`reminder-15-${a.id}`}>J-15</button>
                    <button onClick={()=>sendReminder(a.id, "J-3")} className="px-2 py-1 text-xs rounded bg-[#D96C4A]/10 text-[#D96C4A] hover:bg-[#D96C4A]/20" data-testid={`reminder-3-${a.id}`}>J-3</button>
                    <button onClick={()=>sendReminder(a.id, "45min")} className="px-2 py-1 text-xs rounded bg-[#E3A72F]/10 text-[#E3A72F] hover:bg-[#E3A72F]/20" data-testid={`reminder-45-${a.id}`}>45min</button>
                    <button onClick={()=>onDelete(a.id)} className="text-[#C62828] hover:bg-red-50 p-1 rounded" data-testid={`appt-delete-${a.id}`}><Trash2 className="w-4 h-4"/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      <div className="bg-[#1E4631]/5 border border-[#1E4631]/20 rounded-xl p-5 text-sm text-[#1E4631] flex items-start gap-3">
        <BellRing className="w-5 h-5 mt-0.5"/>
        <div><strong>Rappels automatiques :</strong> Chaque RDV planifié déclenche 3 rappels SMS au patient : 15 jours avant, 3 jours avant et 45 minutes avant. (MODE DÉMO : envoi simulé, intégration Twilio en production.)</div>
      </div>
    </div>
  );
}
